/* ============================================================
   NADI — PDP plan selector

   Keeps the real Shopify form inputs (`id`, `selling_plan`) in sync with
   the tier + purchase-mode radios, and updates the displayed prices.

   All prices originate from Liquid (Shopify + Seal). This file only
   SHOWS numbers that were rendered server-side — it never computes a
   price from a hardcoded rate. The comp's prices were wrong in four
   different ways; live data is the only defence. See tasks/pdp-review.md.
   ============================================================ */

const NADI_BUYBOX_FORM_ID = 'nadi-buybox-form';
const NADI_ATC_EXTERNAL_WINDOW_MS = 750;

/* ------------------------------------------------------------
   Single-flight add-to-cart for the Design 2.0 buy box.

   The buy box is a native `{% form 'product' %}` (POST /cart/add), not a
   Dawn <product-form> wrapper. Seal's app embed (content_for_header,
   often async) also binds that same form: it injects a selling_plan
   input and, once ready, AJAX-posts /cart/add on click/submit.

   One Add to Cart click can therefore fire BOTH:
     1. Seal's fetch to /cart/add.js
     2. the browser's native form POST to /cart/add
   That lands qty 2 / two first-charge prices ($47.40 × 2 = $94.80).
   It is intermittent because Seal's bind races our deferred script:
   a later click, once Seal preventDefault's the native submit, adds
   once. Theme preview makes the race easier to hit.

   This guard:
     - preventDefault + stopImmediatePropagation on the buy-box CTA
       (main button, sticky bar, Enter-to-submit)
     - posts exactly one JSON /cart/add.js from here
     - swallows any other /cart/add during that click (Seal racing us)
     - if Seal's listener ran first in the same click, skip our post
       and just go to cart
   Prices and selling plans are unchanged; we only stop the double post.
   ------------------------------------------------------------ */
function nadiInstallBuyboxAtcGuard() {
  if (window.__nadiAtc && window.__nadiAtc.installed) return window.__nadiAtc;

  const rawFetch = window.fetch.bind(window);
  const rawXhrOpen = XMLHttpRequest.prototype.open;
  const rawXhrSend = XMLHttpRequest.prototype.send;

  const state = {
    installed: true,
    guarding: false,
    inFlight: false,
    lastExternalAddAt: 0,
    rawFetch,
  };
  window.__nadiAtc = state;

  const cartAddUrl = (url) => {
    if (!url) return false;
    try {
      return /\/cart\/add(?:\.js)?(?:\?|$|#)/.test(String(url));
    } catch (e) {
      return false;
    }
  };

  const requestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  };

  const swallowedAdd = () =>
    Promise.resolve(
      new Response('{}', {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      })
    );

  window.fetch = function nadiGuardedFetch(input, init) {
    const url = requestUrl(input);
    if (cartAddUrl(url)) {
      if (state.guarding) return swallowedAdd();
      state.lastExternalAddAt = Date.now();
    }
    return rawFetch(input, init);
  };

  XMLHttpRequest.prototype.open = function nadiGuardedXhrOpen(method, url) {
    this.__nadiCartAdd = cartAddUrl(url);
    return rawXhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function nadiGuardedXhrSend() {
    if (this.__nadiCartAdd) {
      if (state.guarding) {
        try {
          this.abort();
        } catch (e) {
          /* ignore */
        }
        return;
      }
      state.lastExternalAddAt = Date.now();
    }
    return rawXhrSend.apply(this, arguments);
  };

  const buyboxForm = () => document.getElementById(NADI_BUYBOX_FORM_ID);

  const isBuyboxAtcEvent = (event) => {
    const form = buyboxForm();
    if (!form) return false;
    if (event.type === 'submit') return event.target === form;
    const target = event.target && event.target.closest
      ? event.target.closest('[type="submit"], [data-cta], [data-sticky-cta]')
      : null;
    if (!target || target.disabled) return false;
    return (
      form.contains(target) ||
      target.getAttribute('form') === NADI_BUYBOX_FORM_ID ||
      target.hasAttribute('data-sticky-cta')
    );
  };

  const onBuyboxAtcIntent = (event) => {
    if (!isBuyboxAtcEvent(event)) return;
    /* Arm the network swallow BEFORE other listeners on this same click. */
    state.guarding = true;
    event.preventDefault();
    event.stopImmediatePropagation();
    const form = buyboxForm();
    const selector = form && form.querySelector('nadi-plan-selector');
    if (selector && typeof selector.addToCart === 'function') {
      selector.addToCart();
      return;
    }
    /* Custom element not upgraded yet: disable the native POST (already
       preventDefaulted) and let connectedCallback flush a pending add. */
    if (form) form.setAttribute('data-nadi-pending-atc', '1');
  };

  window.addEventListener('click', onBuyboxAtcIntent, true);
  window.addEventListener('submit', onBuyboxAtcIntent, true);

  return state;
}

nadiInstallBuyboxAtcGuard();

class NadiPlanSelector extends HTMLElement {
  connectedCallback() {
    /* Seal rewrites this form and can re-attach the element. Binding
       twice would stack scroll/change/submit listeners. */
    if (this._connected) return;
    this._connected = true;

    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    this.variantInput = this.querySelector('[data-variant-input]');
    this.cta = this.querySelector('[data-cta]');
    this.ctaPrice = this.querySelector('[data-cta-price]');
    this.saveLine = this.querySelector('[data-saveline]');
    this.renewal = this.querySelector('[data-renewal]');
    this.form = this.closest('form');

    this.tierInputs = Array.from(this.querySelectorAll('input[name="tier"]'));
    this.modeInputs = Array.from(this.querySelectorAll('input[name="purchase_mode"]'));

    /* Sticky mobile ATC bar (rendered by the section, outside this element).
       Its button submits the same form via the HTML form attribute, so the
       only job here is showing it after the main CTA scrolls out of view
       and keeping its price display in sync with render(). */
    this.stickyBar = document.querySelector('[data-sticky-atc]');
    this.stickyPrice = this.stickyBar ? this.stickyBar.querySelector('[data-sticky-price]') : null;
    this.stickyCta = this.stickyBar ? this.stickyBar.querySelector('[data-sticky-cta]') : null;
    if (this.stickyBar && this.cta) {
      /* Mobile: show whenever the buy-box CTA is not on screen — below the
         fold on first paint (so a price + add-to-cart is always in view) and
         above it after scrolling past. Desktop (990px+): the full buy box is
         already beside the gallery in the first viewport, so a bar on first
         paint is redundant and covers the hero; show it only once the CTA
         has been scrolled PAST (its box fully above the viewport). A
         throttled scroll listener, not an IntersectionObserver: Seal
         rewrites this form after mount, and a plain listener keeps working
         no matter what happens to the observed node. One
         getBoundingClientRect per 100ms while scrolling is cheap, and a
         timeout (unlike rAF) also runs in throttled/background tabs. */
      this._stickyTick = false;
      this.onScroll = () => {
        if (this._stickyTick) return;
        this._stickyTick = true;
        setTimeout(() => {
          this._stickyTick = false;
          const rect = this.cta.getBoundingClientRect();
          const desktop = window.matchMedia('(min-width: 990px)').matches;
          const hideBar = desktop
            ? rect.bottom > 0
            : rect.top < window.innerHeight && rect.bottom > 0;
          this.stickyBar.hidden = hideBar;
          /* Reserve bottom padding (see nadi-pdp-hero.css) so the bar
             never covers the disclaimer strip or footer bottom row. */
          document.documentElement.classList.toggle('nadi-has-sticky-atc', !hideBar);
        }, 100);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    }

    this._onChange = (e) => {
      if (e.target.name === 'tier' || e.target.name === 'purchase_mode') {
        e.stopPropagation();
        this.render();
      }
    };
    this.addEventListener('change', this._onChange);

    /* Mode/tier clicks must never submit the form. Pointer events on
       .nadi-mode__name previously fell through to the ATC button. */
    this._onClick = (e) => {
      if (e.target.closest('button[type="submit"]')) return;
      if (e.target.closest('.nadi-mode') || e.target.closest('.nadi-tier')) {
        e.stopPropagation();
      }
    };
    this.addEventListener('click', this._onClick);

    /* Seal's embed rewrites this form asynchronously after we mount: it deletes
       our selling_plan input, injects its own, and re-renders its widget. Watch
       for that and re-assert our value, otherwise the plan silently reverts to
       empty and a "Subscribe & Save" click adds a full-price ONE-TIME purchase.
       Verified against a live cart, 2026-07-15. */
    this.observer = new MutationObserver(() => {
      if (this._writing) return;
      this.syncPlanInput();
    });
    if (this.form) this.observer.observe(this.form, { childList: true, subtree: true });

    /* Seal (and anything else) calling form.submit() bypasses submit
       listeners. Route that path through the same single-flight add. */
    if (this.form && !this._nativeSubmit) {
      this._nativeSubmit = this.form.submit.bind(this.form);
      this.form.submit = () => this.addToCart();
    }

    this.render();

    if (this.form && this.form.getAttribute('data-nadi-pending-atc') === '1') {
      this.form.removeAttribute('data-nadi-pending-atc');
      this.addToCart();
    }
  }

  disconnectedCallback() {
    this._connected = false;
    if (this.observer) this.observer.disconnect();
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }
    if (this._onChange) this.removeEventListener('change', this._onChange);
    if (this._onClick) this.removeEventListener('click', this._onClick);
    if (this.form && this._nativeSubmit) {
      this.form.submit = this._nativeSubmit;
      this._nativeSubmit = null;
    }
  }

  /* Never cache this node. Seal replaces it. */
  get planInput() {
    return (
      this.querySelector('input[name="selling_plan"]') ||
      (this.form && this.form.querySelector('input[name="selling_plan"]'))
    );
  }

  get selectedTier() {
    return this.tierInputs.find((i) => i.checked) || this.tierInputs.find((i) => !i.disabled);
  }

  syncPlanInput() {
    const tier = this.selectedTier;
    if (!tier) return;
    const useSub = this.isSubscribe && Boolean(tier.dataset.planId);
    const target = this.planInput;
    if (!target) return;
    const want = useSub ? tier.dataset.planId : '';
    if (target.value === want) return;
    this._writing = true;
    target.value = want;
    this._writing = false;
  }

  get isSubscribe() {
    const m = this.modeInputs.find((i) => i.checked);
    return m ? m.value === 'subscribe' : false;
  }

  /* One variant, qty 1, selling_plan only when Subscribe is selected.
     Built from the radios — not FormData — so a Seal-injected duplicate
     `id` / `selling_plan` / `quantity` field cannot post a second line. */
  buildPayload() {
    const tier = this.selectedTier;
    const id = Number(
      (this.variantInput && this.variantInput.value) || (tier && tier.dataset.variantId) || 0
    );
    const payload = { id, quantity: 1 };
    if (this.isSubscribe && tier && tier.dataset.planId) {
      payload.selling_plan = Number(tier.dataset.planId);
    }
    return payload;
  }

  setSubmitting(on) {
    this._submitting = on;
    const soldOut = Boolean(this.selectedTier && this.selectedTier.disabled);
    if (this.cta) this.cta.disabled = on || soldOut;
    if (this.stickyCta) this.stickyCta.disabled = on || soldOut;
  }

  goToCart() {
    window.location.assign((window.routes && window.routes.cart_url) || '/cart');
  }

  addToCart() {
    const atc = nadiInstallBuyboxAtcGuard();
    if (atc.inFlight) return;
    atc.inFlight = true;
    atc.guarding = true;
    this.setSubmitting(true);
    this.syncPlanInput();

    /* Seal's click listener registered before ours already posted. */
    if (atc.lastExternalAddAt && Date.now() - atc.lastExternalAddAt < NADI_ATC_EXTERNAL_WINDOW_MS) {
      this.goToCart();
      return;
    }

    const payload = this.buildPayload();
    if (!payload.id) {
      atc.inFlight = false;
      atc.guarding = false;
      this.setSubmitting(false);
      return;
    }

    const addBase = ((window.routes && window.routes.cart_add_url) || '/cart/add').replace(/\.js$/, '');
    atc
      .rawFetch(`${addBase}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (data && data.status) {
          throw new Error(data.description || data.message || 'Unable to add to cart');
        }
        this.goToCart();
      })
      .catch((err) => {
        console.error(err);
        atc.inFlight = false;
        atc.guarding = false;
        this.setSubmitting(false);
        if (this.saveLine) {
          this.saveLine.textContent = (err && err.message) || 'Unable to add to cart. Please try again.';
        }
      });
  }

  /* Shopify money formatting. Prices arrive in cents from Liquid. */
  money(cents) {
    const amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const noDecimals = Math.round(cents / 100).toLocaleString('en-US');
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, noDecimals);
  }

  render() {
    const tier = this.selectedTier;
    if (!tier) return;

    const sub = this.isSubscribe;
    const hasPlan = Boolean(tier.dataset.planId);
    const useSub = sub && hasPlan;

    /* --- keep the posted inputs honest --- */
    /* Guarded: Seal rewrites this form asynchronously (see observer above)
       and may momentarily detach our hidden input. */
    if (this.variantInput) this.variantInput.value = tier.dataset.variantId;
    this.syncPlanInput();

    /* --- per-tier price display: subscribe vs one-time --- */
    this.tierInputs.forEach((input) => {
      const label = input.closest('.nadi-tier');
      if (!label) return;
      const tierHasPlan = Boolean(input.dataset.planId);
      const showSub = sub && tierHasPlan;
      const subPrices = label.querySelector('[data-prices-sub]');
      const onePrices = label.querySelector('[data-prices-onetime]');
      const s = label.querySelector('[data-price-sub]');
      const o = label.querySelector('[data-price-onetime]');
      const was = label.querySelector('[data-was]');
      const pmS = label.querySelector('[data-permonth-sub]');
      const pmO = label.querySelector('[data-permonth-onetime]');
      if (subPrices) subPrices.hidden = !showSub;
      if (onePrices) onePrices.hidden = showSub;
      if (s) s.hidden = !showSub;
      if (o) o.hidden = showSub;
      if (was) was.hidden = !showSub;
      if (pmS) pmS.hidden = !showSub;
      if (pmO) pmO.hidden = showSub;
    });

    /* --- CTA --- */
    const firstCharge = useSub
      ? parseInt(tier.dataset.planFirstPrice, 10)
      : parseInt(tier.dataset.onetimePrice, 10);

    const submitting = Boolean(this._submitting);
    if (this.cta) this.cta.disabled = submitting || Boolean(tier.disabled);
    if (this.ctaPrice) this.ctaPrice.textContent = tier.disabled ? '' : ` · ${this.money(firstCharge)}`;
    if (this.stickyPrice) this.stickyPrice.textContent = tier.disabled ? '' : this.money(firstCharge);
    if (this.stickyCta) this.stickyCta.disabled = submitting || Boolean(tier.disabled);

    /* --- save line: states the ACTUAL first charge, not a marketing rate --- */
    if (this.saveLine) {
      if (tier.disabled) {
        this.saveLine.textContent = '';
      } else if (useSub) {
        const oneTime = parseInt(tier.dataset.onetimePrice, 10);
        const saved = oneTime - firstCharge;
        if (saved > 0) {
          const pct = Math.round((saved / oneTime) * 100);
          this.saveLine.textContent = `You save ${this.money(saved)} (${pct}%) on this order.`;
        } else {
          this.saveLine.textContent = '';
        }
      } else {
        this.saveLine.textContent = '';
      }
    }

    /* --- renewal cadence ---
       UI copy uses the selected tier's supply months (`data-months` from
       nadi.supply_months): every 2 / 4 / 6 months. Seal may still bill
       in weeks so deliveries arrive before you run out. Do not change
       Seal intervals from the theme. */
    if (this.renewal) {
      if (useSub) {
        const recurring = parseInt(tier.dataset.planRecurringPrice, 10);
        const months = parseInt(tier.dataset.months, 10);
        const cadence = months === 1 ? 'every month' : `every ${months > 0 ? months : 2} months`;
        this.renewal.textContent = `Then ${this.money(recurring)} ${cadence}. Skip, pause, or cancel anytime.`;
        this.renewal.hidden = false;
      } else {
        this.renewal.textContent = '';
        this.renewal.hidden = true;
      }
    }
  }
}

if (!customElements.get('nadi-plan-selector')) {
  customElements.define('nadi-plan-selector', NadiPlanSelector);
}
